import { useState } from 'react';
import { UserAllocation } from '@/types';
import { getUsersWithProject } from '@/utils/chartUtils';

interface ProjectHighlightState {
  highlightedProject: string | null;
  highlightedUsers: Set<string>;
}

interface ProjectHighlightHook extends ProjectHighlightState {
  highlightProject: (projectKey: string, userAllocations: UserAllocation[]) => void;
  clearHighlight: () => void;
  isHighlighted: (projectKey: string | null) => boolean;
  isUserHighlighted: (userId: string) => boolean;
}

/**
 * Custom hook to manage project highlighting across components
 */
export function useProjectHighlight(): ProjectHighlightHook {
  const [state, setState] = useState<ProjectHighlightState>({
    highlightedProject: null,
    highlightedUsers: new Set<string>()
  });

  const highlightProject = (projectKey: string, userAllocations: UserAllocation[]) => {
    setState({
      highlightedProject: projectKey,
      highlightedUsers: getUsersWithProject(projectKey, userAllocations)
    });
  };

  const clearHighlight = () => {
    setState({
      highlightedProject: null,
      highlightedUsers: new Set()
    });
  };

  const isHighlighted = (projectKey: string | null) => {
    return state.highlightedProject === null || state.highlightedProject === projectKey;
  };

  const isUserHighlighted = (userId: string) => {
    return state.highlightedProject === null || state.highlightedUsers.has(userId);
  };

  return {
    ...state,
    highlightProject,
    clearHighlight,
    isHighlighted,
    isUserHighlighted
  };
}